/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type FC, useEffect, useRef } from 'react';
import {
	type RecaptchaResult,
	RecaptchaTermsAndPolicy,
	useInvisibleRecaptcha,
} from '@atlassian/google-recaptcha';
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AnalyticsActionSubject } from '../../types';
import { getRecaptchaSiteKey } from '../../utils';
import { ANALYTICS_CHANNEL } from '../../analytics/constants';
import { recaptchaLoaded } from '../../analytics/ufoExperiences';
import { useInvitePeopleContext } from './InvitePeopleContext';

const styles = cssMap({
	wrapper: {
		color: token('color.text.subtlest', '#626F86'),
		font: token(
			'font.body.small',
			'normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
	},
});

export const Recaptcha: FC<{
	isLoading: boolean;
	onChange: (result: RecaptchaResult) => void;
}> = ({ isLoading, onChange }) => {
	const { source } = useInvitePeopleContext();
	const { isRecaptchaLoaded, getRecaptchaToken, recaptchaLoadErrorReason } = useInvisibleRecaptcha({
		siteKey: getRecaptchaSiteKey(),
		displayBadge: false,
		analyticsChannel: ANALYTICS_CHANNEL,
		analyticsSource: `${AnalyticsActionSubject.CORE_INVITE_COMPONENT}_${source}`,
	});

	const isInitialLoad = useRef(true);

	useEffect(() => {
		if (isInitialLoad.current) {
			recaptchaLoaded.start();
			isInitialLoad.current = false;
		}
		if (isRecaptchaLoaded) {
			recaptchaLoaded.success();
		}
		if (recaptchaLoadErrorReason) {
			recaptchaLoaded.failure();
		}
		onChange({
			isRecaptchaLoaded,
			getRecaptchaToken,
			recaptchaLoadErrorReason,
		});
	}, [onChange, isRecaptchaLoaded, getRecaptchaToken, recaptchaLoadErrorReason]);

	return (
		<Box testId="recaptcha-footer" data-loading={isLoading} xcss={styles.wrapper}>
			<RecaptchaTermsAndPolicy />
		</Box>
	);
};
